import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  GoogleMap,
  Marker,
  DirectionsService,
  DirectionsRenderer,
  InfoWindow,
  useLoadScript,
  InfoBox,
} from '@react-google-maps/api';
import { GOOGLE_API_KEY } from '../../../constants/googleapi';
import { usePosition } from 'use-position';
import { useSelector } from 'react-redux';
import { getOrderState } from '../../../store/orders/selector';
import { colors } from '../../../constants/colors';
import { Orders } from '../../../store/orders/types';
import { getAppGlobalState } from '../../../store/app-globals/selectors';
import { calcCrow } from '../../dashboard/components/DesktopMealCarousel';

interface CenterProps {
  lat: number;
  lng: number;
}

const containerStyle = {
  height: '300px',
};

const divStyle = {
  backgroundColor: 'white',
  border: '1px solid #ccc',
  padding: '11px 15px',
  borderRadius: 7,
  color: colors.black,
};

interface MapOrderProps {
  order?: Orders;
}

const Map: React.FC<MapOrderProps> = ({ order }) => {
  const { userinfo } = useSelector(getOrderState);
  const { isLoading } = useSelector(getAppGlobalState);
  const count = useRef(0);

  const settings = useMemo(
    () => ({ enableHighAccuracy: true, timeout: 0, maximumAge: Infinity }),
    []
  );
  const watch = true;
  const { latitude, longitude } = usePosition(watch, settings);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_API_KEY,
  });

  const [map, setMap] = React.useState(null);
  const [cooksLocation, setCooksLocation] = useState<CenterProps | undefined>();
  const [origin, setOrigin] = React.useState<CenterProps | undefined>();
  const [direction, setDirection] = React.useState<
    google.maps.DirectionsResult
  >();
  const [isVisible, setVisible] = useState(true);

  const onLoad = React.useCallback(function callback(map) {
    // setMap(map);
    setDirection(undefined);
  }, []);

  const onUnmount = React.useCallback(function callback(map) {
    setMap(map);
    setDirection(undefined);
    setCooksLocation(undefined);
  }, []);

  const cookLocation = () => {
    if (userinfo && !isLoading) {
      const location = {
        lat: userinfo.location.lat || 0,
        lng: userinfo.location.lng || 0,
      };
      setCooksLocation(location);
    }
  };

  const orderExists = () => {
    if (order) {
      const location = {
        lat: order.cookDetails?.location.lat || 0,
        lng: order.cookDetails?.location.lng || 0,
      };
      setCooksLocation(location);
    }
  };

  const usersCurrentLocation = () => {
    if (latitude && longitude) {
      if (latitude !== origin?.lat && longitude !== origin?.lng) {
        const lat = latitude;
        const lng = longitude;
        setOrigin({ lat, lng });
      }
    }
  };

  const directionServiceCallback = (response: any, status: any) => {
    if (response !== null) {
      if (status === 'OK' && count.current === 0) {
        count.current++;
        setDirection(response);
      }
    }
  };

  useEffect(cookLocation, [userinfo, isLoading]);
  useEffect(orderExists, [order]);
  useEffect(usersCurrentLocation, [latitude, longitude]);
  useEffect(() => {
    // console.log('on Cooks location change', cooksLocation);
  }, [cooksLocation]);
  return (
    <React.Fragment>
      {isLoaded && cooksLocation ? (
        <GoogleMap
          mapContainerStyle={containerStyle}
          mapContainerClassName="map-container"
          center={cooksLocation}
          zoom={15}
          onLoad={onLoad}
          onUnmount={onUnmount}
        >
          {!direction && (
            <Marker
              position={cooksLocation}
              onClick={() => setVisible(!isVisible)}
            >
              {cooksLocation && isVisible && (
                <InfoWindow
                  position={cooksLocation}
                  onCloseClick={() => setVisible(false)}
                >
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 'bold' }}>
                      Cooks Location
                    </div>
                  </div>
                </InfoWindow>
              )}
            </Marker>
          )}

          {origin && cooksLocation && (
            <DirectionsService
              options={{
                destination: cooksLocation,
                origin: origin,
                travelMode:
                  window.google && window.google.maps.TravelMode.DRIVING,
              }}
              onUnmount={directionsRenderer => {
                // setDirection(undefined);
              }}
              callback={directionServiceCallback}
            />
          )}

          {direction && (
            <React.Fragment>
              <DirectionsRenderer
                directions={direction}
                options={{}}
                onUnmount={directionsRenderer => {
                  setDirection(undefined);
                }}
              />
              <InfoBox
                position={cooksLocation}
                options={{ closeBoxURL: '', enableEventPropagation: true }}
              >
                <div style={divStyle}>
                  <div
                    style={{
                      fontSize: 14,
                      textAlign: 'center',
                      fontWeight: 'bold',
                    }}
                  >
                    {`Cooks Location: ${calcCrow(
                      cooksLocation.lat,
                      cooksLocation.lng,
                      latitude,
                      longitude
                    ).toFixed(2)}`}
                  </div>
                </div>
              </InfoBox>
            </React.Fragment>
          )}
        </GoogleMap>
      ) : (
        <div>{`Can't load the map right now`}</div>
      )}
    </React.Fragment>
    // </LoadScript>
  );
};

export default React.memo(Map);
